import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Toast } from 'primereact/toast';

import ApiService from '../services/ApiService';
import { useAuth } from '../context/auth';

import MachineHero from './components/MachineHero';
import GeneralInfo from './components/GeneralInfo';
import EditForm from './components/EditForm';
import ActionsBar from './components/ActionsBar';
import MachineAccessSettings from './components/MachineAccessSettings';
import MachineOperationalScheduleSettings from './components/MachineOperationalScheduleSettings';
import {
	copyMachineAccess,
	machineAccessEquals,
	normalizeMachineAccess,
	serializeMachineAccess,
	validateMachineAccess,
} from './machineAccess';
import {
	copyOperationalSchedule,
	createOperationalSchedule,
	operationalScheduleEquals,
	serializeOperationalSchedule,
	validateOperationalSchedule,
} from './machineOperationalSchedule';

import './MachineDetail.css';

const api = new ApiService();

const PAYMENT_OPTIONS = [
	{ value: 'MAESTRO', label: 'Maestro', icon: 'maestro.png' },
	{ value: 'VISA', label: 'Visa', icon: 'visa.png' },
	{ value: 'MASTERCARD', label: 'Mastercard', icon: 'mastercard.png' },
	{ value: 'EDENRED', label: 'Edenred', icon: 'edenred.png' },
	{ value: 'SODEXO', label: 'Sodexo', icon: 'sodexo.png' },
	{ value: 'MONIZZE', label: 'Monizze', icon: 'monizze.png' },
	{ value: 'BANCONTACT', label: 'Bancontact', icon: 'bancontact.png' },
	{ value: 'PLUXEE', label: 'Pluxee', icon: 'pluxee.png' },
];
const PAYMENT_OPTION_VALUES = PAYMENT_OPTIONS.map(({ value }) => value);

const MACHINE_NON_LANGUAGE_FIELDS = [
	'name',
	'price',
	'currency',
	'availableForCustomer',
	'mobileActive',
	'type',
	'referenceIntake',
	'theme',
	'steamDeviation',
	'blendDeviation',
	'enabledPaymentOptions',
	'operationalStatusNote',
	'cupConfigurationId',
	'soupOffsetPrice',
	'qrCodeUrl',
];

const MachineDetail = (props) => {
	// Ref for toast notifications - used to show success/error messages
	const toast = useRef(null);

	// Static options for dropdowns - these don't change during component lifecycle
	const typeOptions = [
		{ key: 'smoothie', value: 'SMOOTHIE', text: 'Smoothie' },
		{ key: 'soup', value: 'SOUP', text: 'Soup' },
		{ key: 'combo', value: 'COMBO', text: 'Combo' },
	];

	// Main form data state - organized as a single object for easy management
	const [formData, setFormData] = useState({
		theme: null, // UI theme for the machine interface ('Alberts' or 'Alpro')
		name: '', // Customer-facing name of the machine
		productionName: '', // Internal production name
		price: 0, // Base price for products
		currency: '', // Currency code (e.g., 'EUR', 'USD')
		availableForCustomer: false, // Whether the machine is active in the field
		type: '', // Machine type (SMOOTHIE, SOUP, COMBO)
		referenceIntake: null, // Nutritional reference intake settings
		steamDeviation: 0, // Fine-tuning for steam temperature
		blendDeviation: 0, // Fine-tuning for blending process
		enabledPaymentOptions: PAYMENT_OPTION_VALUES, // Logos displayed by the POS card-payment screen
		operationalStatusNote: '', // Free-text note about the machine's current operational status
		cupConfigurationId: '', // Selected cup configuration
		soupOffsetPrice: 0.0, // Additional price for soup products
		mobileActive: false, // Whether the machine is active in the mobile app or the QR code on the machine interface
		qrCodeUrl: '',
		preferredLanguageId: '', // Language selected by default on the machine
		allowedLanguageIds: [], // Languages that users can select on the machine
	});

	// Options for dynamic dropdowns - loaded from API
	const [referenceIntakeOptions, setReferenceIntakeOptions] = useState([]);
	const [cupConfigurationOptions, setCupConfigurationOptions] = useState([]);
	const [themeOptions, setThemeOptions] = useState([]);
	const [languageOptions, setLanguageOptions] = useState([]);

	// UI state
	const [loading, setLoading] = useState(false);
	const [locationFullAddress, setLocationFullAddress] = useState('');
	const [locationId, setLocationId] = useState('');
	const [showSaveButton, setShowSaveButton] = useState(false);
	const [originalFormData, setOriginalFormData] = useState(null);
	const [machineAccess, setMachineAccess] = useState(null);
	const [originalMachineAccess, setOriginalMachineAccess] = useState(null);
	const [machineAccessLoading, setMachineAccessLoading] = useState(true);
	const [machineAccessLoadError, setMachineAccessLoadError] = useState(false);
	const [machineAccessReloadToken, setMachineAccessReloadToken] = useState(0);
	const [machineAccessRecipeOptions, setMachineAccessRecipeOptions] = useState([]);
	const [operationalSchedule, setOperationalSchedule] = useState(null);
	const [originalOperationalSchedule, setOriginalOperationalSchedule] = useState(null);
	const [operationalScheduleLoading, setOperationalScheduleLoading] = useState(true);
	const [operationalScheduleLoadError, setOperationalScheduleLoadError] = useState(false);
	const [operationalScheduleIsNew, setOperationalScheduleIsNew] = useState(false);
	const [operationalScheduleReloadToken, setOperationalScheduleReloadToken] = useState(0);
	const [operationalClosures, setOperationalClosures] = useState([]);
	const [operationalClosuresLoading, setOperationalClosuresLoading] = useState(true);
	const [operationalClosuresLoadError, setOperationalClosuresLoadError] = useState(false);
	const [operationalClosuresReloadToken, setOperationalClosuresReloadToken] = useState(0);
	const isThemeMissing = formData.theme == null;
	const hasNonLanguageChanges =
		!originalFormData ||
		MACHINE_NON_LANGUAGE_FIELDS.some(
			(fieldName) => JSON.stringify(formData[fieldName]) !== JSON.stringify(originalFormData[fieldName]),
		);
	const hasLanguageChanges =
		!originalFormData ||
		formData.preferredLanguageId !== originalFormData.preferredLanguageId ||
		JSON.stringify(formData.allowedLanguageIds) !== JSON.stringify(originalFormData.allowedLanguageIds);
	const requiresThemeForMachineUpdate = isThemeMissing && hasNonLanguageChanges;
	const machineAccessValidation = useMemo(
		() =>
			machineAccess
				? validateMachineAccess(machineAccess)
				: { isValid: true, errors: {}, globalErrors: [], firstMessage: '' },
		[machineAccess],
	);
	const hasMachineAccessChanges = !machineAccessEquals(machineAccess, originalMachineAccess);
	const operationalScheduleValidation = useMemo(
		() =>
			operationalSchedule
				? validateOperationalSchedule(operationalSchedule)
				: { isValid: true, errors: {}, firstMessage: '' },
		[operationalSchedule],
	);
	const hasOperationalScheduleChanges = !operationalScheduleEquals(operationalSchedule, originalOperationalSchedule);
	const isSaveDisabled =
		requiresThemeForMachineUpdate ||
		(hasMachineAccessChanges && !machineAccessValidation.isValid) ||
		(hasOperationalScheduleChanges && !operationalScheduleValidation.isValid);
	const saveDisabledTooltip = requiresThemeForMachineUpdate
		? 'Select a UI theme before saving'
		: machineAccessValidation.firstMessage || operationalScheduleValidation.firstMessage || 'Complete required fields before saving';
	const visibleMachineAccessRecipeOptions = useMemo(() => {
		const knownRecipeIds = new Set(machineAccessRecipeOptions.map((option) => option.value));
		const configuredRecipeIds = machineAccess
			? machineAccess.profiles.reduce((ids, profile) => [...ids, ...profile.recipeIds], [])
			: [];
		const unavailableOptions = [...new Set(configuredRecipeIds)]
			.filter((recipeId) => !knownRecipeIds.has(recipeId))
			.map((recipeId) => ({ label: `Unavailable recipe (${recipeId})`, value: recipeId }));

		return [...machineAccessRecipeOptions, ...unavailableOptions];
	}, [machineAccess, machineAccessRecipeOptions]);

	// Authentication context
	const { authTokens } = useAuth();

	/**
	 * Generic handler for form field changes
	 * This centralizes all form updates and makes the code cleaner
	 * Also shows save button when changes are made
	 *
	 * @param {string} fieldName - Name of the field to update
	 * @param {any} value - New value for the field
	 */
	const handleFieldChange = (fieldName, value) => {
		setFormData((prev) => ({
			...prev,
			[fieldName]: value,
		}));
		setShowSaveButton(true);
	};

	const handleMachineAccessChange = (configuration) => {
		setMachineAccess(configuration);
		setShowSaveButton(true);
	};

	const handleOperationalScheduleChange = (schedule) => {
		setOperationalSchedule(schedule);
		setShowSaveButton(true);
	};

	const sortOperationalClosures = (closures) =>
		[...closures].sort((left, right) => left.startsOnLocal.localeCompare(right.startsOnLocal));

	const replaceOperationalClosure = (closure) => {
		setOperationalClosures((current) =>
			sortOperationalClosures([...current.filter((entry) => entry.closureId !== closure.closureId), closure]),
		);
	};

	const createOperationalClosure = async (parameters) => {
		const response = await api.createMachineOperationalClosure(
			props.location.state.machineId,
			parameters,
			{ 'X-Firebase-Auth-Client': authTokens.token },
		);
		replaceOperationalClosure(response.data);
		toast.current.show({ severity: 'success', summary: 'Special closure added', detail: 'The machine will be closed for the selected dates.' });
	};

	const updateOperationalClosure = async (closureId, parameters) => {
		const response = await api.updateMachineOperationalClosure(
			props.location.state.machineId,
			closureId,
			parameters,
			{ 'X-Firebase-Auth-Client': authTokens.token },
		);
		replaceOperationalClosure(response.data);
		toast.current.show({ severity: 'success', summary: 'Special closure updated', detail: 'The selected closure has been updated.' });
	};

	const deleteOperationalClosure = async (closureId) => {
		await api.deleteMachineOperationalClosure(props.location.state.machineId, closureId, {
			'X-Firebase-Auth-Client': authTokens.token,
		});
		setOperationalClosures((current) => current.filter((closure) => closure.closureId !== closureId));
		toast.current.show({ severity: 'success', summary: 'Special closure removed', detail: 'The machine will follow its normal schedule for those dates.' });
	};

	/**
	 * Keep the preferred language in the machine's allowed-language selection.
	 * A user must select a different preferred language before removing the current one.
	 */
	const handleAllowedLanguagesChange = (allowedLanguageIds) => {
		if (formData.preferredLanguageId && !allowedLanguageIds.includes(formData.preferredLanguageId)) {
			const preferredLanguage = languageOptions.find(
				(languageOption) => languageOption.value === formData.preferredLanguageId,
			);
			const preferredLanguageName = preferredLanguage?.text || 'The preferred language';

			toast.current.show({
				severity: 'warn',
				summary: 'Preferred language cannot be removed',
				detail: `${preferredLanguageName} is set as preferred and cannot be removed from the allowed languages. Select another preferred language first.`,
				life: 5000,
			});
			return;
		}

		if (allowedLanguageIds.length === 0) {
			toast.current.show({
				severity: 'warn',
				summary: 'Allowed languages required',
				detail: 'At least one language must be available on the machine.',
			});
			return;
		}

		handleFieldChange('allowedLanguageIds', allowedLanguageIds);
	};

	/**
	 * Handle form submission to save machine changes
	 * This function sends the form data to the API and shows appropriate feedback
	 */
	const handleSubmit = async () => {
		if (requiresThemeForMachineUpdate) {
			toast.current.show({
				severity: 'warn',
				summary: 'Theme required',
				detail: 'Select a UI theme before saving the machine.',
			});
			return;
		}

		if (hasMachineAccessChanges && !machineAccessValidation.isValid) {
			toast.current.show({
				severity: 'warn',
				summary: 'Invalid machine access settings',
				detail: machineAccessValidation.firstMessage,
			});
			return;
		}

		if (hasOperationalScheduleChanges && !operationalScheduleValidation.isValid) {
			toast.current.show({
				severity: 'warn',
				summary: 'Invalid operational schedule',
				detail: operationalScheduleValidation.firstMessage,
			});
			return;
		}

		if (
			(hasNonLanguageChanges || hasLanguageChanges) &&
			(!formData.preferredLanguageId ||
				!formData.allowedLanguageIds.length ||
				!formData.allowedLanguageIds.includes(formData.preferredLanguageId))
		) {
			toast.current.show({
				severity: 'warn',
				summary: 'Invalid language settings',
				detail: 'Select a preferred language and make sure it is included in the allowed languages.',
			});
			return;
		}

		setLoading(true);
		const headers = {
			'X-Firebase-Auth-Client': authTokens.token,
		};
		const machineId = props.location.state.machineId;
		const languageData = {
			preferredLanguageId: formData.preferredLanguageId,
			allowedLanguageIds: formData.allowedLanguageIds,
		};
		const data = {
			name: formData.name,
			price: formData.price,
			currency: formData.currency,
			availableForCustomer: formData.availableForCustomer,
			type: formData.type,
			referenceIntakeId: formData.referenceIntake?.id,
			theme: formData.theme,
			steamDeviation: formData.steamDeviation,
			blendDeviation: formData.blendDeviation,
			enabledPaymentOptions: formData.enabledPaymentOptions,
			operationalStatusNote: formData.operationalStatusNote,
			cupConfigurationId: formData.cupConfigurationId,
			soupOffsetPrice: formData.soupOffsetPrice,
			mobileActive: formData.mobileActive,
			qrCodeUrl: formData.qrCodeUrl,
			language: languageData,
		};
		let saveTarget = hasNonLanguageChanges || hasLanguageChanges ? 'machine' : 'machine access';

		try {
			if (hasNonLanguageChanges) {
				// The full machine update requires and persists the nested language settings too.
				await api.updateMachine(machineId, data, headers);
				setOriginalFormData({ ...formData });
			} else if (hasLanguageChanges) {
				// For language-only changes, use the dedicated admin/operator endpoint.
				await api.updateMachineLanguages(machineId, languageData, headers);
				setOriginalFormData({ ...formData });
			}

			if (hasMachineAccessChanges) {
				saveTarget = 'machine access';
				const response = await api.updateMachineAccessProfiles(
					machineId,
					serializeMachineAccess(machineAccess),
					headers,
				);
				const savedMachineAccess = normalizeMachineAccess(response.data);
				setMachineAccess(savedMachineAccess);
				setOriginalMachineAccess(copyMachineAccess(savedMachineAccess));
			}

			if (hasOperationalScheduleChanges) {
				saveTarget = 'operational schedule';
				const response = await api.updateMachineOperationalSchedule(
					machineId,
					serializeOperationalSchedule(operationalSchedule),
					headers,
				);
				const savedOperationalSchedule = copyOperationalSchedule(response.data);
				setOperationalSchedule(savedOperationalSchedule);
				setOriginalOperationalSchedule(savedOperationalSchedule);
				setOperationalScheduleIsNew(false);
			}

			setShowSaveButton(false);
			toast.current.show({
				severity: 'success',
				summary: 'Success',
				detail: 'Machine settings have been updated successfully.',
			});
		} catch (error) {
			toast.current.show({
				severity: 'error',
				summary: 'Error',
				detail: error.message || `Failed to update ${saveTarget} settings. Please try again.`,
			});
			console.error('Error updating machine:', error);
		} finally {
			setLoading(false);
		}
	};

	/**
	 * Reset form to original values
	 * This restores the form to the state when it was last saved
	 */
	const handleReset = () => {
		if (originalFormData) {
			setFormData({ ...originalFormData });
		}
		if (originalMachineAccess) {
			setMachineAccess(copyMachineAccess(originalMachineAccess));
		}
		if (originalOperationalSchedule) {
			setOperationalSchedule(copyOperationalSchedule(originalOperationalSchedule));
		}
		setShowSaveButton(false);
		toast.current.show({
			severity: 'warn',
			summary: 'Reset',
			detail: 'All changes have been reset to last saved state',
			life: 3000,
		});
	};

	/**
	 * Load reference intake options on component mount
	 * Reference intakes define nutritional standards for different regions
	 */
	useEffect(() => {
		const headers = {
			'X-Firebase-Auth-Client': authTokens.token,
		};

		const loadReferenceIntakes = async () => {
			try {
				const response = await api.listReferenceIntakes(headers);
				const options = response.data.map((intake) => ({
					key: intake.id,
					value: intake,
					text: intake.country || 'Unknown Country',
				}));
				setReferenceIntakeOptions(options);
			} catch (error) {
				console.error('Failed to load reference intakes:', error);
				toast.current.show({
					severity: 'warn',
					summary: 'Warning',
					detail: 'Failed to load reference intake options',
				});
			}
		};

		if (authTokens.token) {
			loadReferenceIntakes();
		}
	}, [authTokens.token]);

	/**
	 * Load the complete language catalogue. Machine-specific allowed and preferred
	 * language settings are loaded with the machine details below.
	 */
	useEffect(() => {
		const headers = {
			'X-Firebase-Auth-Client': authTokens.token,
		};

		const loadLanguages = async () => {
			try {
				const response = await api.getLanguages(headers);
				const options = response.data.map((language) => ({
					key: language.id,
					value: language.id,
					text: `${language.name.charAt(0).toUpperCase()}${language.name.slice(1)} (${language.code.toUpperCase()})`,
				}));
				setLanguageOptions(options);
			} catch (error) {
				console.error('Failed to load languages:', error);
				toast.current.show({
					severity: 'warn',
					summary: 'Warning',
					detail: 'Failed to load language options',
				});
			}
		};

		if (authTokens.token) {
			loadLanguages();
		}

		return () => {
			setLanguageOptions([]);
		};
	}, [authTokens.token]);

	/**
	 * Load machine data when component mounts or machine ID changes
	 * This fetches all machine details and populates the form
	 */
	useEffect(() => {
		const machineId = props.location.state.machineId;
		if (!machineId) return;

		setLoading(true);
		const headers = {
			'X-Firebase-Auth-Client': authTokens.token,
		};

		const loadMachineData = async () => {
			try {
				const response = await api.getMachine(machineId, headers);
				const machine = response.data;
				console.log('Loaded machine data:', machine);

				// Update form data with machine information
				const initialFormData = {
					name: machine.customerName || '',
					productionName: machine.name || '',
					price: machine.pricing?.original || 0,
					currency: machine.pricing?.currency || '',
					availableForCustomer: machine.availableForCustomer || false,
					mobileActive: machine.mobileActive || false,
					type: machine.type || '',
					referenceIntake: machine.referenceIntake || null,
					theme: machine.theme ?? null,
					steamDeviation: machine.steamDeviation || 0,
					blendDeviation: machine.blendDeviation || 0,
					enabledPaymentOptions: machine.enabledPaymentOptions ?? PAYMENT_OPTION_VALUES,
					operationalStatusNote: machine.operationalStatusNote || '',
					cupConfigurationId: machine.cupConfigurationId || '',
					soupOffsetPrice: machine.soupOffsetPrice || 0.0,
					qrCodeUrl: machine.qrCodeUrl || 'https://app.alberts.be',
					preferredLanguageId: machine.language?.preferred?.id || '',
					allowedLanguageIds: machine.language?.allowed?.map((language) => language.id) || [],
				};

				setFormData(initialFormData);
				setOriginalFormData({ ...initialFormData });

				// Set location data separately as it's not part of the form
				setLocationFullAddress(machine.location?.fullAddress || '');
				setLocationId(machine.location?.locationId || '');
			} catch (error) {
				console.error('Failed to load machine data:', error);
				toast.current.show({
					severity: 'error',
					summary: 'Error',
					detail: 'Failed to load machine details. Please refresh the page.',
				});
			} finally {
				setLoading(false);
			}
		};

		loadMachineData();
	}, [props.location.state.machineId, authTokens.token]);

	/**
	 * Load the access snapshot separately because it has its own full-replacement API.
	 * The technician PIN is retained in state for unchanged saves but is never rendered.
	 */
	useEffect(() => {
		const machineId = props.location.state.machineId;
		if (!machineId || !authTokens.token) return;

		let active = true;
		const headers = {
			'X-Firebase-Auth-Client': authTokens.token,
		};

		const loadMachineAccess = async () => {
			setMachineAccessLoading(true);
			setMachineAccessLoadError(false);
			try {
				const response = await api.getMachineAccessProfiles(machineId, headers);
				if (!active) return;
				const configuration = normalizeMachineAccess(response.data);
				setMachineAccess(configuration);
				setOriginalMachineAccess(copyMachineAccess(configuration));
			} catch (error) {
				if (!active) return;
				setMachineAccess(null);
				setOriginalMachineAccess(null);
				setMachineAccessLoadError(true);
				console.error('Failed to load machine access settings:', error);
			} finally {
				if (active) {
					setMachineAccessLoading(false);
				}
			}
		};

		loadMachineAccess();
		return () => {
			active = false;
		};
	}, [props.location.state.machineId, authTokens.token, machineAccessReloadToken]);

	/**
	 * Operational hours are maintained through a separate versioned schedule API.
	 * A missing schedule (404) is a normal state: the operator can create the first one from the tab.
	 */
	useEffect(() => {
		const machineId = props.location.state.machineId;
		if (!machineId || !authTokens.token) return;

		let active = true;
		const headers = {
			'X-Firebase-Auth-Client': authTokens.token,
		};

		const loadOperationalSchedule = async () => {
			setOperationalScheduleLoading(true);
			setOperationalScheduleLoadError(false);
			try {
				const response = await api.getMachineOperationalSchedule(machineId, headers);
				if (!active) return;
				const schedule = copyOperationalSchedule(response.data);
				setOperationalSchedule(schedule);
				setOriginalOperationalSchedule(schedule);
				setOperationalScheduleIsNew(false);
			} catch (error) {
				if (!active) return;
				if (error.status === 404) {
					const schedule = createOperationalSchedule();
					setOperationalSchedule(schedule);
					setOriginalOperationalSchedule(copyOperationalSchedule(schedule));
					setOperationalScheduleIsNew(true);
				} else {
					setOperationalSchedule(null);
					setOriginalOperationalSchedule(null);
					setOperationalScheduleLoadError(true);
					console.error('Failed to load operational schedule:', error);
				}
			} finally {
				if (active) {
					setOperationalScheduleLoading(false);
				}
			}
		};

		loadOperationalSchedule();
		return () => {
			active = false;
		};
	}, [props.location.state.machineId, authTokens.token, operationalScheduleReloadToken]);

	useEffect(() => {
		const machineId = props.location.state.machineId;
		if (!machineId || !authTokens.token) return;

		let active = true;
		const loadOperationalClosures = async () => {
			setOperationalClosuresLoading(true);
			setOperationalClosuresLoadError(false);
			try {
				const response = await api.getMachineOperationalClosures(machineId, {
					'X-Firebase-Auth-Client': authTokens.token,
				});
				if (active) {
					setOperationalClosures(sortOperationalClosures(response.data));
				}
			} catch (error) {
				if (active) {
					setOperationalClosuresLoadError(true);
					console.error('Failed to load special closures:', error);
				}
			} finally {
				if (active) {
					setOperationalClosuresLoading(false);
				}
			}
		};

		loadOperationalClosures();
		return () => {
			active = false;
		};
	}, [props.location.state.machineId, authTokens.token, operationalClosuresReloadToken]);

	/**
	 * Recipe names make per-profile restrictions understandable. An empty
	 * selection deliberately means that the profile can order every recipe.
	 */
	useEffect(() => {
		const machineId = props.location.state.machineId;
		if (!machineId || !authTokens.token) return;

		let active = true;
		const headers = {
			'X-Firebase-Auth-Client': authTokens.token,
		};

		const loadMachineAccessRecipes = async () => {
			try {
				const [recipeCatalogueResponse, machineRecipesResponse] = await Promise.all([
					api.getAllMachineRecipesWithoutPagination(headers),
					api.getMachineRecipes(machineId, headers),
				]);
				if (!active) return;
				const machineRecipeIds = new Set(machineRecipesResponse.data.map((recipe) => recipe.recipeId));
				const options = recipeCatalogueResponse.data.recipes
					.filter((recipe) => machineRecipeIds.has(recipe.id))
					.map((recipe) => ({
						label: recipe.name || recipe.id,
						value: recipe.id,
					}))
					.sort((left, right) => left.label.localeCompare(right.label));
				setMachineAccessRecipeOptions(options);
			} catch (error) {
				console.error('Failed to load recipe options for machine access:', error);
			}
		};

		loadMachineAccessRecipes();
		return () => {
			active = false;
			setMachineAccessRecipeOptions([]);
		};
	}, [props.location.state.machineId, authTokens.token]);

	/**
	 * Load cup configuration options on component mount
	 */
	useEffect(() => {
		const headers = {
			'X-Firebase-Auth-Client': authTokens.token,
		};

		const loadCupConfigurations = async () => {
			try {
				const response = await api.getAllCupConfigurations(headers);
				const configurations = response.data;

				const options = configurations.map((config) => ({
					key: config.id,
					value: config.id,
					text: `${config.name} (Cup size: ${config.cupSize} ml)`,
				}));

				setCupConfigurationOptions(options);
			} catch (error) {
				console.error('Failed to load cup configurations:', error);
				toast.current.show({
					severity: 'warn',
					summary: 'Warning',
					detail: 'Failed to load cup configuration options',
				});
			}
		};

		if (authTokens.token) {
			loadCupConfigurations();
		}

		// Cleanup function to reset options when component unmounts
		return () => {
			setCupConfigurationOptions([]);
		};
	}, [authTokens.token]);

	/**
	 * Load cup theme options on component mount
	 */
	useEffect(() => {
		const headers = {
			'X-Firebase-Auth-Client': authTokens.token,
		};

		const loadThemeOptions = async () => {
			try {
				const response = await api.getMachineThemes(headers);
				const themeData = response.data;
				const themeOptions = themeData.map((themeOption) => ({
					key: themeOption,
					value: themeOption,
					text: `${themeOption}`,
				}));
				setThemeOptions(themeOptions);
			} catch (error) {
				console.error('Failed to load themes:', error);
				toast.current.show({
					severity: 'warn',
					summary: 'Warning',
					detail: 'Failed to load themes',
				});
			}
		};

		if (authTokens.token) {
			loadThemeOptions();
		}

		// Cleanup function to reset options when component unmounts
		return () => {
			setThemeOptions([]);
		};
	}, [authTokens.token]);

	/**
	 * Handle copying text to clipboard
	 * This provides user feedback when copying machine ID or other text
	 *
	 * @param {string} text - The text to copy to clipboard
	 */
	const handleCopyToClipboard = async (text) => {
		if (!navigator.clipboard) {
			// Clipboard API not available - show warning
			toast.current.show({
				severity: 'warn',
				summary: 'Not Supported',
				detail: 'Clipboard copying is not supported in your browser',
			});
			return;
		}

		try {
			await navigator.clipboard.writeText(text);
			toast.current.show({
				severity: 'success',
				summary: 'Copied!',
				detail: 'Text has been copied to clipboard',
			});
		} catch (error) {
			console.error('Failed to copy to clipboard:', error);
			toast.current.show({
				severity: 'error',
				summary: 'Error',
				detail: 'Failed to copy text to clipboard',
			});
		}
	};

	return (
		<div className="machine-detail-page">
			<Toast ref={toast} />

			<ActionsBar
				showSaveButton={showSaveButton}
				onSave={handleSubmit}
				onReset={handleReset}
				disabled={isSaveDisabled}
				disabledTooltip={saveDisabledTooltip}
			/>

			<div className="container-details">
				{/* Left side - Machine information */}
				<div className="info-section">
					{/* Hero section with machine name and type */}
					<MachineHero name={formData.name} productionName={formData.productionName} type={formData.type} />

					{/* General machine information */}
					<GeneralInfo
						productionName={formData.productionName}
						machineId={props.location.state?.machineId}
						plcVersion={props.location.state?.plcVersion}
						posVersion={props.location.state?.posVersion}
						availableForCustomer={formData.availableForCustomer}
						mobileActive={formData.mobileActive}
						ipOpenvpn={props.location.state?.ipOpenvpn}
						locationFullAddress={locationFullAddress}
						locationId={locationId}
						onCopyToClipboard={handleCopyToClipboard}
					/>
				</div>

				{/* Right side - Edit form */}
				<EditForm
					formData={formData}
					typeOptions={typeOptions}
					themeOptions={themeOptions}
					referenceIntakeOptions={referenceIntakeOptions}
					cupConfigurationOptions={cupConfigurationOptions}
					languageOptions={languageOptions}
					paymentOptionOptions={PAYMENT_OPTIONS}
					loading={loading}
					showSaveButton={showSaveButton}
					isThemeMissing={isThemeMissing}
					isSaveDisabled={isSaveDisabled}
					saveDisabledTooltip={saveDisabledTooltip}
					machineAccessSettings={
						<MachineAccessSettings
							section="general"
							value={machineAccess}
							validation={machineAccessValidation}
							recipeOptions={visibleMachineAccessRecipeOptions}
							loading={machineAccessLoading}
							loadError={machineAccessLoadError}
							onChange={handleMachineAccessChange}
							onRetry={() => setMachineAccessReloadToken((current) => current + 1)}
						/>
					}
					operatorAccessSettings={
						<MachineAccessSettings
							section="operators"
							value={machineAccess}
							validation={machineAccessValidation}
							recipeOptions={visibleMachineAccessRecipeOptions}
							loading={machineAccessLoading}
							loadError={machineAccessLoadError}
							onChange={handleMachineAccessChange}
							onRetry={() => setMachineAccessReloadToken((current) => current + 1)}
						/>
					}
					userAccessSettings={
						<MachineAccessSettings
							section="users"
							value={machineAccess}
							validation={machineAccessValidation}
							recipeOptions={visibleMachineAccessRecipeOptions}
							loading={machineAccessLoading}
							loadError={machineAccessLoadError}
							onChange={handleMachineAccessChange}
							onRetry={() => setMachineAccessReloadToken((current) => current + 1)}
						/>
					}
					operationalScheduleSettings={
						<MachineOperationalScheduleSettings
							value={operationalSchedule}
							validation={operationalScheduleValidation}
							loading={operationalScheduleLoading}
							loadError={operationalScheduleLoadError}
							isNew={operationalScheduleIsNew}
							closures={operationalClosures}
							closuresLoading={operationalClosuresLoading}
							closuresLoadError={operationalClosuresLoadError}
							onChange={handleOperationalScheduleChange}
							onRetry={() => setOperationalScheduleReloadToken((current) => current + 1)}
							onCreateClosure={createOperationalClosure}
							onUpdateClosure={updateOperationalClosure}
							onDeleteClosure={deleteOperationalClosure}
							onRetryClosures={() => setOperationalClosuresReloadToken((current) => current + 1)}
						/>
					}
					onFieldChange={handleFieldChange}
					onAllowedLanguagesChange={handleAllowedLanguagesChange}
					onSubmit={handleSubmit}
				/>
			</div>
		</div>
	);
};

export default MachineDetail;
